import CardBox from '@/pages/dashboard/components/CardBox';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import useLegendTooltip from '@/pages/dashboard/components/Chart/hooks/useLegendTooltip';
import './index.scss';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';
import { useIntl } from 'umi';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { ConfigProvider, Select } from 'tea-component';
import { formatOptions } from '@/pages/dashboard/components/NewFilter/util';
import { usePcRevenueSelectorMap } from '@/pages/dashboard/filterPageComponents/monetization/hooks';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import StairsChart from '@/pages/dashboard/components/Chart/components/StairsChart';
import { formatExeclCommon, formatExeclNormalPercent, formatNum, formatSmallPercent, getChartDateFormatString, getSystemColor, handleDownload, unitConversion } from '@/pages/dashboard/utils/utils';
import { addBarChartLabel, xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { newFormatPercentNum, salesOverviewFormatOption, salesOverviewPaddingMap, replacePlaceholders } from './utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import getSystemLegend from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils';
import {
  IChartDataResult,
  IChartOption,
  IChartSeriesItem,
  IChartYAxisItem,
  ICountrySelectorItem,
  IFilterCatalogItem,
  IDimensionDataItem,
  IFilterValue,
  ILegendSelectChangeEvent,
  IMinTableColumn,
  IMetrics,
  ISelectOption,
  ISeriesTooltipRow,
  ISelectedMetricsProps,
  ISeparatedOption,
  ISalesTrendChartProps,
  ITableRowData,
  ITooltipParam,
  ITrendAggregateItem,
} from './type';
import useDimensionData, { useCatalogIcon } from './hooks';
import { getCustomValueColumnTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { resolveConfigString, TMetricsInfo } from '@/pages/dashboard/utils/metricUtils';
import SectionHeadingWithSub from '@/pages/dashboard/components/SectionHeadingWithSub';

const SalesTrendChart: React.FC<ISalesTrendChartProps> = (props) => {
  const { selectedMetrics: selectedMetricsProps, data, changeData, config, className = '' } = props || {};
  const { componentMetrics, otherParams, isHideDiscount, isHideDimensionSelector, customAccumulation, fixedDimensionValue, dimensionColumnName, legendTooltipMap: legendTooltipMapConfig } = config || {};
  const { trends_data: trendsData, discount_trends_data: discountTrendsData, selectedRegion, selectedDimension = 'system', catalogTooltipMap } = (data || {}) as typeof data & { catalogTooltipMap?: Record<string, { en: string; cn: string }> };
  // 优先使用顶层 data 贯穿下来的 catalogTooltipMap（页面级共享配置），兜底 config.legendTooltipMap
  const legendTooltipMap = catalogTooltipMap ?? legendTooltipMapConfig;
  const { filterVal } = useContext(FilterContext);
  const normalizedFilterVal = filterVal as unknown as IFilterValue;
  const selector = Array.isArray(normalizedFilterVal?.selector)
    ? undefined
    : normalizedFilterVal?.selector;
  const { getCodeText } = usePcRevenueSelectorMap(selector);
  const { getWeekDay } = useWeekDay();

  // 从父组件传入的 propsData 中获取 metricsInfo（由配置引擎注入）
  const metricsInfo = data?.metricsInfo as TMetricsInfo | undefined;

  // 解析 componentMetrics 中的模板字符串（兼容旧配置：无 metricsInfo 时原样返回）
  const resolvedComponentMetrics = useMemo(() => {
    if (!componentMetrics || !metricsInfo) return componentMetrics;
    const resolved: Record<string, IMetrics> = {};
    Object.entries(componentMetrics).forEach(([key, metric]) => {
      resolved[key] = {
        ...metric,
        // 解析 name/nameCn 模板，如果是普通字符串则原样返回
        name: resolveConfigString(metric.name, metricsInfo, metric.name),
        nameCn: resolveConfigString(metric.nameCn, metricsInfo, metric.nameCn),
        // 如果有子标题也一并解析
        ...(metric.subName ? { subName: resolveConfigString(metric.subName, metricsInfo, metric.subName) } : {}),
        ...(metric.subNameCn ? { subNameCn: resolveConfigString(metric.subNameCn, metricsInfo, metric.subNameCn) } : {}),
        ...(metric.subTitleEn ? { subTitleEn: resolveConfigString(metric.subTitleEn, metricsInfo, metric.subTitleEn) } : {}),
        ...(metric.subTitleCn ? { subTitleCn: resolveConfigString(metric.subTitleCn, metricsInfo, metric.subTitleCn) } : {}),
        // 解析 formatRule 模板（如 {{metricsInfo.xxx.formatter}}）
        ...(metric.formatRule ? { formatRule: resolveConfigString(metric.formatRule, metricsInfo, metric.formatRule) } : {}),
      };
    });
    return resolved;
  }, [componentMetrics, metricsInfo]);

  // catalog 图标相关逻辑
  const { hasCatalogIcon, catalogNameRender, getCatalogLegend } = useCatalogIcon({
    filterVal: normalizedFilterVal,
    customAccumulation,
  });

  const [iconType, setIconType] = useState('barChart');
  const [iseye, setIseye] = useState(true);
  const [stairsLegend, setStairsLegend] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const { locale, formatMessage } = useIntl();

  const selectedMetricsPropsData = selectedMetricsProps as ISelectedMetricsProps & {
    subTitleEn?: string;
    subTitleCn?: string;
  };
  const selectedMetricsFromConfig = resolvedComponentMetrics?.[selectedMetricsPropsData?.indicatorsName];
  const selectedMetrics: IMetrics | undefined = selectedMetricsFromConfig
    ? {
      ...selectedMetricsFromConfig,
      subTitleEn: selectedMetricsFromConfig.subTitleEn ?? selectedMetricsPropsData?.subTitleEn,
      subTitleCn: selectedMetricsFromConfig.subTitleCn ?? selectedMetricsPropsData?.subTitleCn,
    }
    : undefined;
  // 当前激活图的类型
  const [activeChartTab, setActiveChartTab] = useState(selectedMetrics?.isHideBarChart ? 'lineChart' : 'barChart');

  // 获取维度趋势图数据
  const { SEPARATED_OPTIONS, dimensionData = [], dimensionValue, setDimensionValue, loading, isError, hasRequested } = useDimensionData({
    gameCode: data.gameCode,
    filterVal: normalizedFilterVal,
    selectedMetricId: selectedMetricsPropsData.id,
    otherParams,
    customAccumulation,
    fixedDimensionValue,
  });

  // 获取 SEPARATED_OPTIONS 的第一项的值作为默认值
  const defaultOptionValue = SEPARATED_OPTIONS.length > 0 ? SEPARATED_OPTIONS[0]?.value : undefined;

  // 如果初始化选中配置不存在维度选项里，则自动选中列表第一个选项
  const selectedValue = SEPARATED_OPTIONS.some((item: ISeparatedOption) => item.value === selectedDimension) ? selectedDimension : defaultOptionValue;

  useEffect(() => {
    // 配置了固定维度值时，不覆盖 dimensionValue
    if (!fixedDimensionValue && selectedValue !== undefined) {
      setDimensionValue(selectedValue);
    }
  }, [selectedValue, fixedDimensionValue]);

  const normalizedDimensionList = useMemo(() => {
    const fallbackDimensionList = trendsData?.data?.platform_trends || [];
    if (dimensionData?.length) {
      // 统一兼容 dimension/trends 返回结构，避免直接改写原始接口对象。
      return dimensionData.map((item: IDimensionDataItem) => ({
        date: item?.date,
        dimension: {
          platform: item?.dimension?.dim ?? item?.dimension?.platform,
        },
        metrics: item?.metrics,
      }));
    }

    // 维度请求尚未真正发出前，临时回退主接口里的默认分维度数据，避免首屏空白闪动。
    if (!hasRequested) {
      return fallbackDimensionList;
    }

    // 维度请求返回空数组时，保持空态，不再回退旧数据。
    return [];
  }, [trendsData, dimensionData, hasRequested]);

  const isTrendsError = isError;
  const isTrendsLoading = trendsData?.loading || loading;
  const isDiscountError = !!discountTrendsData?.errorMsg;
  const isDiscountLoading = discountTrendsData?.loading || false;

  const { timeType = 'Day' } = normalizedFilterVal || {};

  function handleAxisFormat(val: string) {
    return timeType === 'Month' ? val : momentFormat(val?.split("(")?.[0], 'MM-DD');
  }

  const correspondingMetrics: IMetrics | undefined = resolvedComponentMetrics?.[resolvedComponentMetrics?.[selectedMetricsPropsData?.indicatorsName]?.correspondingId || ''];

  /**
   * 通用数值格式化辅助函数
   * 优先使用 formatRule + dataFormat，未配置时 fallback 到 isUserType/isPercent 原有逻辑
   */
  const formatValue = (
    value: string | number | null | undefined,
    metrics?: IMetrics,
  ) => {
    if (metrics?.formatRule) {
      return dataFormat({ value: value ?? '', formatRule: metrics.formatRule });
    }
    if (metrics?.isPercent) {
      return formatSmallPercent(Number(value ?? 0), 2, false);
    }
    return formatNum(value ?? '', false, metrics?.isUserType);
  };

  const isEn = locale === LANGUAGE_EN;

  const { onLegendMouseover: handleLegendMouseover, onLegendMouseout: handleLegendMouseout, LegendTooltipBubble } = useLegendTooltip({
    tooltipMap: legendTooltipMap,
    isEN: isEn,
  });

  const title = isEn ? selectedMetrics?.name : selectedMetrics?.nameCn;
  const titleCom = (
    <SectionHeadingWithSub
      title={title}
      subTitleEn={selectedMetrics?.subTitleEn}
      subTitleCn={selectedMetrics?.subTitleCn}
    />
  );
  const hasShare = !!(selectedMetrics?.isHaveShare);
  const shareFormat = selectedMetrics?.shareFormat || 'percent1Decimal';
  const showShareLessValue = selectedMetrics?.showShareLessValue || {
    value: 0.001,
    text: '<0.1%'
  };
  const {
    id: correspondingId,
  } = correspondingMetrics || {};

  const correspondingTitle = isEn ? correspondingMetrics?.name || '' : correspondingMetrics?.nameCn || '';

  const { stairsxAxisData, stairsSeries, stairsLegendMap, stairsNodata } = useMemo(() => {
    const { platform_product_trends: rawStairsList } = discountTrendsData?.data || {};
    const stairsList = rawStairsList || [];
    const stairsSeries: IChartSeriesItem[] = [];
    const stairsTimeList: IDimensionDataItem[] = [];
    stairsList?.forEach((item: IDimensionDataItem) => {
      if (item?.date === stairsList?.[0]?.date) stairsTimeList.push(item);
    });
    const stairsxAxisData = [
      ...new Set(
        stairsList.map((item: IDimensionDataItem) =>
          getChartDateFormatString(timeType, item?.date, true),
        ),
      ),
    ];
    // 阶梯图坐标轴数据
    const stairsLegendDataMap = stairsTimeList?.map((item: IDimensionDataItem) => ({
      platformName: getCodeText('os', item?.dimension?.platform || ''),
      platform: item?.dimension?.platform,
      product: item?.dimension?.product,
    })) || [];

    const stairsLegendData = [
      ...new Set(stairsLegendDataMap?.map((item) => item?.platform)),
    ].filter((item): item is string => Boolean(item));
    const newStairsLegend = stairsLegendData?.map((item: string) => {
      const name = getCodeText('os', item || '');
      return name;
    })?.filter((value): value is string => Boolean(value));
    setStairsLegend(newStairsLegend);
    stairsLegendDataMap?.map((legend) => {
      const name = getCodeText('os', legend?.platform || '');
      const productName = getCodeText('product', legend?.product || '');
      const customColor = getSystemColor(name);
      const xAxisIndex = newStairsLegend?.findIndex((value) => value === legend?.platformName);
      const stairsObj: IChartSeriesItem = {
        name,
        productName,
        type: 'line',
        step: 'middle',
        itemStyle: {
          color: customColor ? customColor : null,
        },
        areaStyle: {
          color: customColor ? customColor : null,
          opacity: 0.5,
        },
        xAxisIndex: xAxisIndex + 1,
        yAxisIndex: xAxisIndex + 2,
        data: [],
      };
      stairsList?.map((item: IDimensionDataItem) => {
        if (item?.dimension?.platform === legend?.platform && item?.dimension?.product === legend?.product) {
          stairsObj.data.push(item?.metrics?.discount?.value ?? '');
        }
        return null;
      });
      stairsObj.data.length && stairsSeries.push(stairsObj);
      return null;
    });

    return {
      stairsxAxisData,
      stairsSeries,
      stairsLegendMap: newStairsLegend,
      stairsNodata: !stairsLegendData?.length
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountTrendsData, locale]);

  // 柱状图和折线图数据
  const { barxAxisData, barLegend, barSeries, lineSeries, barNodata, table } =
    useMemo<IChartDataResult>(() => {
      const { trends: rawAggregateList } = trendsData?.data || {};
      const aggregateList = rawAggregateList || [];
      const dimensionList = normalizedDimensionList;
      const leftMetric = selectedMetrics?.id || 'net_revenue';
      const barxAxisData = aggregateList?.map((item: ITrendAggregateItem) =>
        getChartDateFormatString(timeType, item?.date, true),
      );
      const barLegendDataRaw = [
        ...new Set(dimensionList?.map((item: IDimensionDataItem) => item?.dimension?.platform)),
      ].filter((item): item is string => Boolean(item));
      const catalogOrder = normalizedFilterVal?.filterCatalog?.list?.map((c: IFilterCatalogItem) => c.value) || [];
      const safeIdx = (idx: number) => idx === -1 ? catalogOrder.length : idx;
      const barLegendData = catalogOrder.length
        ? [...barLegendDataRaw].sort((a, b) => safeIdx(catalogOrder.indexOf(a)) - safeIdx(catalogOrder.indexOf(b)))
        : barLegendDataRaw;
      const barLegend = barLegendData?.map((item: string) => {
        const name = getCodeText(dimensionValue, item);
        return name;
      });
      const barSeries: IChartSeriesItem[] = [];
      let isNodata = true;
      // 左侧坐标轴数据
      barLegendData?.map((legend: string) => {
        const name = getCodeText(dimensionValue, legend);
        const customColor = dimensionValue === 'system' ? (getSystemColor(name)) : null;
        const columnObj: IChartSeriesItem = {
          name,
          barGap: '5%',
          barCategoryGap: '50%',
          type: 'bar',
          stack: 'Total',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: [],
          itemStyle: {
            color: customColor ? customColor : null,
          },
        };
        dimensionList?.map((item: IDimensionDataItem) => {
          if (item?.dimension?.platform === legend) {
            columnObj.data.push(item?.metrics?.[leftMetric]?.value ?? '');
          }

          return null;
        });
        if (columnObj.data.length) isNodata = false;
        columnObj.data.length && barSeries.push(columnObj);

        return null;
      });
      // 右侧坐标轴数据
      const lineObj: IChartSeriesItem = {
        name: correspondingTitle,
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 1,
        data: [],
        itemStyle: {
          color: '#7F6FF1',
        },
      };

      aggregateList?.map((item: ITrendAggregateItem) => {
        if (selectedMetrics?.correspondingId) {
          lineObj.data.push(
            item?.metrics?.[selectedMetrics?.correspondingId]?.value ?? '',
          );
        }

        return null;
      });

      if (lineObj.data.length) {
        barSeries.push(lineObj);
        isNodata = false;
        if (correspondingMetrics) {
          barLegend?.push(correspondingTitle);
        }
      }
      // 无数据添加x轴
      if (isNodata) {
        stairsxAxisData?.map(() => {
          lineObj.data.push('');
          return null;
        });
        barSeries.push(lineObj);

        if (correspondingMetrics) {
          barLegend?.push(correspondingTitle);
        }
      }

      const linexAxisData = aggregateList.map((item: ITrendAggregateItem) =>
        getChartDateFormatString(timeType, item?.date, true),
      );

      const lineLegendDataRaw = [
        ...new Set(dimensionList?.map((item: IDimensionDataItem) => item?.dimension?.platform)),
      ].filter((item): item is string => Boolean(item));
      const lineLegendData = catalogOrder.length
        ? [...lineLegendDataRaw].sort((a, b) => safeIdx(catalogOrder.indexOf(a)) - safeIdx(catalogOrder.indexOf(b)))
        : lineLegendDataRaw;
      const lineLegend = lineLegendData?.map((item: string) => {
        const name = getCodeText(dimensionValue, item);
        return name;
      });
      const lineSeries: IChartSeriesItem[] = [];

      // 左侧坐标轴数据
      lineLegendData?.map((legend: string) => {
        const name = getCodeText(dimensionValue, legend);
        const customColor = dimensionValue === 'system' ? getSystemColor(name) : '';
        const columnObj: IChartSeriesItem = {
          name,
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: [],
          itemStyle: {
            color: customColor ? customColor : null,
          },
        };
        dimensionList?.map((item: IDimensionDataItem) => {
          if (item?.dimension?.platform === legend) {
            columnObj.data.push(item?.metrics?.[leftMetric]?.value ?? '');
          }

          return null;
        });
        if (columnObj.data.length) isNodata = false;
        columnObj.data.length && lineSeries.push(columnObj);

        return null;
      });


      // 表头固定列
      const dimensionHeader = dimensionColumnName
        ? (isEn ? dimensionColumnName.en : dimensionColumnName.cn)
        : formatMessage({ id: `common_${dimensionValue}` });
      const tableOption: IMinTableColumn[] = [
        {
          key: 'date',
          align: 'left',
          fixed: 'left',
          width: 160,
          header: formatMessage({ id: 'monetization_revenue_Date' }),
          render: (cvm: ITableRowData) => {
            const weekDay = getWeekDay(cvm?.date);
            const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
            return <>
              <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
              {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
            </>;
          },
        },
        {
          key: 'platform',
          align: 'right',
          header: dimensionHeader,
        },
        {
          key: 'value',
          align: 'right',
          header: title,
        },
      ];

      // if(hasShare) {
      //   tableOption.push({
      //     key: 'share',
      //     align: 'right',
      //     header: formatMessage({ id: 'common_share'}),
      //   });
      // }
      const tableData: ITableRowData[] = [];
      const downloadData: ITableRowData[] = [];
      dimensionList?.map((item: IDimensionDataItem) => {
        const date = getChartDateFormatString(timeType, item?.date, true);
        const obj: ITableRowData = { date, platform: '', value: '' };
        const downObj: ITableRowData = { date, platform: '', value: '' };
        const metricValue = item?.metrics?.[leftMetric]?.value;
        obj.value = formatValue(item?.metrics?.[leftMetric]?.value, selectedMetrics);
        downObj.value = selectedMetrics?.isPercent
          ? formatExeclNormalPercent(metricValue ?? '-')
          // 这里通过 unknown 过渡，兼容 utils.ts 中遗留的过窄签名声明。
          : formatExeclCommon(metricValue as unknown as null);
        const platform = getCodeText(dimensionValue, item?.dimension?.platform || '');
        obj.platform = platform;
        downObj.platform = platform;
        // obj.share = dataFormat({value: item?.metrics?.[leftMetric]?.share_off_all, formatRule: shareFormat  });
        // downObj.share = dataFormat({value: item?.metrics?.[leftMetric]?.share_off_all, formatRule: shareFormat  });
        tableData.push(obj);
        downloadData.push(downObj);

        return null;
      });

      // 当value等于0，不用展示
      const updatedBarSeries = barSeries.map((series: IChartSeriesItem) => ({
        ...series,
        data: series.data.map((value) => parseFloat(String(value)) === 0 ? '' : value),
      }));
      const updatedLineSeries = lineSeries.map((series: IChartSeriesItem) => ({
        ...series,
        data: series.data.map((value) => parseFloat(String(value)) === 0 ? '' : value),
      }));

      return {
        barxAxisData,
        barLegend,
        barSeries: updatedBarSeries,
        linexAxisData,
        lineLegend,
        lineSeries: updatedLineSeries,
        barNodata: isNodata,
        table: {
          option: tableOption,
          data: tableData,
          downloadData,
        },
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trendsData, timeType, selectedMetrics, stairsxAxisData, locale, normalizedDimensionList, normalizedFilterVal?.filterCatalog]);

  const handleLegendSelectChange = (
    event: ILegendSelectChangeEvent,
    opt: IChartOption,
  ) => {
    if (dimensionValue !== 'system') return; // 非平台维度，点击图例不影响折扣图
    const newSelected = event?.selected || {};
    const newLengend: string[] = [];
    Object.keys(newSelected)?.forEach((item: string) => {
      if (outName?.includes(item) || !newSelected[item]) return;
      if (stairsLegendMap?.includes(item)) newLengend?.push(item);
    });
    // 这里同步图例选中态，保持收入图和折扣图联动。
    opt.legend = opt.legend
      ? { ...opt.legend, selected: newSelected }
      : opt.legend;
    setSelected(newSelected);
    setStairsLegend(newLengend);
  };

  const series = useMemo(() => [...barSeries, ...stairsSeries], [barSeries, stairsSeries]);

  const newLineSeries = useMemo(() => [...lineSeries, ...stairsSeries], [lineSeries, stairsSeries]);

  const outName = ['Total', correspondingTitle];
  const isNodata = timeType === 'Day' ? barNodata && stairsNodata : barNodata; // 周月颗粒度不展示折扣图

  let legendData;

  if (barNodata) {
    legendData = stairsLegendMap;
  } else if (stairsLegendMap && stairsLegendMap.length > barLegend.length - 1 && dimensionValue === 'system') {
    legendData = [...stairsLegendMap, correspondingTitle]; // 平台维度需同步折扣图图例
  } else {
    legendData = barLegend;
  }

  const xAxisData = barNodata ? stairsxAxisData : barxAxisData;

  const isShow = isHideDiscount ? false : (timeType === 'Day' ? !isTrendsLoading && !isNodata && !isTrendsError : false); // isHideDiscount 或 周月颗粒度不展示折扣图

  const stairsExtraTop = 36;

  let height = 495;
  if (stairsLegend?.length && isShow) {
    height = 90 + 275 + 50 + 110 * stairsLegend?.length + stairsExtraTop;
  } else if (timeType !== 'Day' || isHideDiscount) {
    height = 400;
  }

  const subTitle = useMemo(() => {
    const { sum, avg } = trendsData.data || {};
    const name = (isEn ? selectedMetrics?.subName : selectedMetrics?.subNameCn) || title;
    let selectedMetricsData: { name: string; num: string | number; }[] = [];

    if (selectedMetrics) {
      if (!selectedMetrics.isPercent) {
        if (selectedMetrics.isHideSum) {
          selectedMetricsData = [
            {
              name: `${name} ${formatMessage({ id: 'pc_console_avg' })}`,
              num: formatValue(avg?.[selectedMetrics.id]?.value || '', selectedMetrics),
            },
          ];
        } else {
          selectedMetricsData = [
            {
              name: `${name} ${formatMessage({ id: 'pc_console_sum' })}`,
              num: formatValue(sum?.[selectedMetrics.id]?.value || '', selectedMetrics),
            },
            {
              name: formatMessage({ id: 'pc_console_avg' }),
              num: formatValue(avg?.[selectedMetrics.id]?.value || '', selectedMetrics),
            },
          ];
        }
      }
    }

    let correspondingData: { name: string; num: string | number; }[] = [];

    if (!selectedMetrics?.isPercent && correspondingId) {
      if (correspondingMetrics?.isHideSum) {
        correspondingData = [
          {
            name: `${correspondingTitle} ${formatMessage({ id: 'pc_console_avg' })}`,
            num: `${formatValue(avg?.[correspondingId]?.value || '', correspondingMetrics)}`,
          },
        ];
      } else {
        correspondingData = [
          {
            name: `${correspondingTitle} ${formatMessage({ id: 'pc_console_sum' })}`,
            num: `${formatValue(sum?.[correspondingId]?.value || '', correspondingMetrics)}`,
          },
          {
            name: `${formatMessage({ id: 'pc_console_avg' })}`,
            num: `${formatValue(avg?.[correspondingId]?.value || '', correspondingMetrics)}`,
          },
        ];
      }
    }


    return [
      ...selectedMetricsData,
      ...correspondingData
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsData, selectedMetrics, locale]);

  const countryOption = useMemo<ISelectOption[]>(() => formatOptions({
    options: (selector?.country_list || []) as ICountrySelectorItem[],
    locale,
    valueKey: 'country_code',
    cnKey: 'country_name_ch',
    enKey: 'country_name_en',
  }), [selector, locale]);

  const buttonValue = countryOption?.find(v => v?.value === selectedRegion)?.text;

  const salesOverviewFormatyAxis = (
    opt: IChartOption,
    legend: string[],
    hasDiscond = true,
  ) => {
    const newyAxis: IChartYAxisItem[] = [
      {
        type: 'value',
        gridIndex: 0,
        position: 'left',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed' as const,
            color: '#ddd',
          },
        },
        axisLabel: {
          color: '#888888',
          fontSize: 12,
          formatter: (value: string | number) => {
            if (selectedMetrics?.id === 'accumulated_refund_rate' || selectedMetrics?.id === 'lifetime_refund_rate') {
              return formatSmallPercent(Number(value ?? 0), 2, true);
            }
            return unitConversion(value);
          }
        }

      },
      {
        type: 'value',
        gridIndex: 0,
        position: 'right',
        alignTicks: true, // 多Y轴时，开启是否对齐刻度线
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed' as const,
            color: '#ddd',
          },
        },
        axisLabel: {
          formatter: (value: string | number) => unitConversion(value),
        },
      },
    ];
    if (hasDiscond) {
      newyAxis.push(...legend?.map((item: string, index: number) => {
        const name = getCodeText('os', item || '').replace(/\s*/g, '');
        const enPadding = salesOverviewPaddingMap?.[name] || (40 - 7 * name?.length);

        return {
          type: 'value' as const,
          name: `${formatMessage({ id: 'common_discount' })}(${name})`,
          gridIndex: index + 1,
          axisLabel: {
            formatter: (value: string | number) => {
              const numericValue = Number(value || 0);
              const num = numericValue > 0 && numericValue < 0.01 ? 2 : 0;
              const val = (numericValue && `${newFormatPercentNum(numericValue, num)}`) || value;
              return val;
            },
          },
          splitNumber: 2,
          nameLocation: 'end' as const,
          nameTextStyle: {
            padding: locale === LANGUAGE_EN ? [0, enPadding, 0, 0] : [0, enPadding + 20, 0, 0],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed' as const,
              color: '#ddd',
            },
          },
        };
      }));
    }
    // eslint-disable-next-line no-param-reassign
    opt.yAxis = newyAxis;
    return opt;
  };

  const formatterFunc = (params: ITooltipParam[]) => {
    // 鼠标位置
    const top = params?.[0]?.axisIndex === 0;
    const newParams = params?.filter((item) => (top ? item?.axisIndex === 0 : item?.axisIndex));
    if (top) return barFormatter(newParams);
    return stairsFormatter(newParams);
  };

  const lineFormatterFunc = (params: ITooltipParam[]) => {
    // 鼠标位置
    const top = params?.[0]?.axisIndex === 0;
    const newParams = params?.filter((item) => (top ? item?.axisIndex === 0 : item?.axisIndex));
    if (top) return lineFormatter(newParams);
    return stairsFormatter(newParams);
  };

  // 浮窗value列自定义
  const valueRender = (value: string, row: ISeriesTooltipRow) => {
    const { orgData } = row;
    const metrics = (orgData?.seriesType === 'bar' || row?._key === 'sum') ? selectedMetrics : correspondingMetrics;
    // 汇总行且指标配置了 isHideSum 时，隐藏汇总值但保留标题行
    if (row?._key === 'sum' && selectedMetrics?.isHideSum) {
      return '';
    }
    return formatValue(value, metrics);
  };

  const barFormatter = (params: ITooltipParam[]) => {

    if (dimensionValue === 'system') {
      const noSortKeys: string[] = [];
      const noShareKeys: string[] = [];
      const newParams = params?.map((item: ITooltipParam) => {
        const newItem = {
          ...item,
        };
        // 折线列入特殊数据范围
        if (item?.seriesType === 'line') {
          newItem.iconType = 'line';
          noSortKeys.push(item.seriesName);
        }
        if (item.seriesName === correspondingTitle && hasShare) {
          noShareKeys?.push(item.seriesName);
        }
        return newItem;
      });
      return getCustomValueColumnTooltip({
        params: newParams, options: {
          hasShare,
          showShareLessValue: hasShare ? showShareLessValue : {},
          valueNameCn: '',
          valueNameEn: '',
          isEn,
          isPlatform: true,
          sumNameCn: title,
          sumNameEn: title,
          notIncludedInSumKeys: noSortKeys,
          noSortKeys,
          noShareKeys,
          isHideNull: true,
          shareFormatRule: shareFormat,
        }
      }, valueRender);
    }

    const noSortKeys: string[] = ['Others', 'others', '其他'];
    const notIncludedInSumKeys: string[] = [];
    const noShareKeys: string[] = [];
    // ?.filter((item: { seriesName: string; }) => item?.seriesName !== correspondingTitle)
    let newParams = params?.map((item: ITooltipParam) => {
      const newItem = {
        ...item,
      };
      // // 折线列入特殊数据范围
      if (item?.seriesType === 'line') {
        newItem.iconType = 'line';
        noSortKeys.push(item.seriesName);
        notIncludedInSumKeys.push(item.seriesName);
      }
      if (item.seriesName === correspondingTitle && hasShare) {
        noShareKeys?.push(item.seriesName);
      }
      return newItem;
    });

    const nameArr = (newParams || []).map((item: ITooltipParam) => item?.seriesName || '');
    if (nameArr?.includes('Others') || nameArr?.includes('其他')) {
      newParams = newParams?.filter((item: { seriesName: string; }) => item?.seriesName !== correspondingTitle);
    }

    return getCustomValueColumnTooltip({
      params: newParams, options: {
        hasShare,
        showShareLessValue: hasShare ? showShareLessValue : {},
        valueNameCn: '',
        valueNameEn: '',
        isEn,
        sumNameCn: title,
        sumNameEn: title,
        noSortKeys,
        noShareKeys,
        notIncludedInSumKeys,
        isHideNull: true,
        shareFormatRule: shareFormat,
        nameRender: hasCatalogIcon ? catalogNameRender : undefined,
      }
    }, valueRender);
  };

  const stairsFormatter = (params: ITooltipParam[]) => {
    const data = params.map(({ seriesName, value, color }, index) => {
      const metricsNum = value === '' ? '' : newFormatPercentNum(Number(value), 1);
      return {
        name: stairsSeries?.[index]?.productName || '',
        customColorName: seriesName?.toLowerCase()?.replace(/\s/g, '') || '',
        metricsNum,
        icon: getSystemFlag(seriesName) || '',
        customColor: getSystemColor(seriesName),
        color,
      };
    });
    // 过滤0的数据
    const newData = data?.filter((item) => item?.metricsNum !== '0.0%');
    const sortData = newData?.sort((a, b) => {
      // 确保同一颜色按 metricsNum 排序
      if (a.customColorName === b.customColorName) {
        return Number(String(b.metricsNum)?.replace(/,|-|%|/g, '')) - Number(String(a.metricsNum)?.replace(/,|-|%|/g, ''));
      }
      // 对于不同颜色，按名称排序
      return 1;
    });

    return getEchartTooltipText({
      className: 'dbd-sales-netrevenue-tooltip',
      title: params?.[0]?.axisValue,
      metricsName: formatMessage({ id: 'common_discount' }),
      data: sortData,
      isRemoveNull: true,
    });
  };

  const lineFormatter = (params: ITooltipParam[]) => {
    // 浮窗value列格式化
    const lineValueRender = (value: string | number, row?: ISeriesTooltipRow) => {
      // 汇总行且指标配置了 isHideSum 时，隐藏汇总值但保留标题行
      if (row?._key === 'sum' && selectedMetrics?.isHideSum) {
        return '';
      }
      // 特殊指标保留原有专用逻辑
      if (selectedMetrics?.id === 'asp') {
        return formatNum(value, false, false);
      }
      if (selectedMetrics?.id === 'accumulated_refund_rate' || selectedMetrics?.id === 'lifetime_refund_rate') {
        return formatSmallPercent(Number(value), 2, false);
      }
      return formatValue(value, selectedMetrics);
    };

    // 平台走新的浮窗
    if (dimensionValue === 'system') {
      return getCustomValueColumnTooltip({
        params, options: {
          hasShare,
          showShareLessValue: hasShare ? showShareLessValue : {},
          hasSum: false,
          valueNameCn: '',
          valueNameEn: '',
          isEn,
          valueFormatRule: '',
          isPlatform: true,
          sumNameCn: title,
          sumNameEn: title,
          isHideNull: true,
          shareFormatRule: shareFormat,
        }
      }, lineValueRender);
    }

    return getCustomValueColumnTooltip({
      params, options: {
        hasShare,
        showShareLessValue: hasShare ? showShareLessValue : {},
        hasSum: true,
        valueNameCn: '',
        valueNameEn: '',
        isEn,
        valueFormatRule: '',
        isPlatform: false,
        sumNameCn: title,
        sumNameEn: title,
        isHideNull: true,
        shareFormatRule: shareFormat,
        nameRender: hasCatalogIcon ? catalogNameRender : undefined,
      }
    }, lineValueRender);
  };

  const getSeriesData = (option: IChartOption) => {
    const stackName = 'Total';
    const newSeries: IChartSeriesItem[] = [];

    option.series.forEach((series: IChartSeriesItem) => {
      const modifiedSeries = { ...series };
      if (
        series.type === 'bar'
        && series.data
        && (
          JSON.stringify(option.legend?.selected) === '{}'
          || option.legend?.selected?.[series.name]
        )
      ) {
        if (series.stack) modifiedSeries.stack = stackName;
        newSeries.push(modifiedSeries);
      } else {
        const newTotalData = Array(option.series[0]?.data?.length).fill(0);
        modifiedSeries.data = newTotalData;
        newSeries.push(modifiedSeries);
      }
    });

    return newSeries.map(series => series.data);
  };


  // 双轴自定义配置
  const formatterConfig = useMemo(() => ({
    stairsFormatter: formatterFunc,
    lineStairsFormatter: lineFormatterFunc,
    setOtherConfigFunc: (opt: IChartOption) => {
      let updatedOpt = opt;
      updatedOpt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
      updatedOpt = xAxisHighLight(opt, {
        formatter: (params: { value: string; }) => handleAxisFormat(params.value)
      });
      if (hasCatalogIcon) {
        getCatalogLegend(opt);
      } else if (selector) {
        getSystemLegend(opt, selector);
      }
      // 取消图例不显示该折扣图数据（依赖stairsLegend去重新生成series）
      let newSeries: IChartSeriesItem[] = [];
      updatedOpt.series?.map((item: IChartSeriesItem) => {
        if (!item?.step) return newSeries?.push(item);
        if (stairsLegend?.includes(item?.name)) {
          const xAxisIndex = stairsLegend?.findIndex(v => v === item?.name);
          return newSeries?.push({
            ...item,
            xAxisIndex: xAxisIndex + 1,
            yAxisIndex: xAxisIndex + 2,
          });
        }

        return null;
      });

      // 周月需过滤掉折扣图数据
      if (timeType !== 'Day') {
        newSeries = newSeries.filter((item: IChartSeriesItem) => !item?.step);
      }

      // 针对双Y轴的折线图图例
      if (updatedOpt.legend?.data) {
        const lineLegendNames = barSeries
          ?.filter((item: IChartSeriesItem) => item?.type === 'line')
          ?.map((item: IChartSeriesItem) => item?.name);
        updatedOpt.legend.data = updatedOpt.legend.data?.map((item) => {
          if (typeof item !== 'string' && lineLegendNames.includes(item.name)) {
            return {
              name: item.name,
              icon: 'path://M0 0 H12 V2 H0 Z', // 横线svg path
              itemStyle: {
                color: '#7F6FF1'
              }
            };
          }
          return item;
        });
      }

      // 收入图表无数据 或者 折扣图平台比收入平台多 的情况下，需要补齐series才能显示全图例
      if (barNodata || stairsLegendMap?.length > barLegend?.length - 1) {
        const noSeleectList: string[] = [];
        Object.keys(selected)?.map((item: string) => {
          if (outName?.includes(item)) return null;
          if (!selected[item]) noSeleectList?.push(item);

          return null;
        });
        noSeleectList?.forEach((item: string) => {
          const serics = {
            ...opt.series[0],
            name: item,
          };
          // 折扣图图例全取消,切为折线图的情况下
          if (iconType === 'lineChart' && stairsLegend.length === 0) {
            serics.xAxisIndex = 0;
            serics.yAxisIndex = 1;
          }
          newSeries?.push(serics);
        });
      }
      updatedOpt.series = newSeries;
      if (updatedOpt.legend) {
        updatedOpt.legend.selected = selected;
      }
      updatedOpt = addBarChartLabel(opt, {
        show: iseye && !selectedMetrics?.isHideSum, seriesData: getSeriesData(opt),
        formatNum: (value: string | number) =>
          formatValue(value, selectedMetrics)

      });

      // eslint-disable-next-line no-param-reassign
      opt.extraTop = stairsExtraTop;

      // 周月颗粒度不显示折扣图
      updatedOpt = salesOverviewFormatOption(opt, stairsLegend, timeType === 'Day');
      updatedOpt = salesOverviewFormatyAxis(opt, stairsLegend, timeType === 'Day');
      return updatedOpt;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [selector, barNodata, stairsLegendMap?.length, barLegend?.length, selected, iseye, stairsLegend, salesOverviewFormatyAxis, outName, selectedMetrics?.isUserType]);


  const renderBarChartList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <StairsChart
        key={String(isEn)}
        height={height - 12}
        isStackBar={true}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? series : []}
        formatterFunc={formatterConfig?.stairsFormatter}
        setOtherConfigFunc={formatterConfig?.setOtherConfigFunc}
        handleLegendSelectChange={handleLegendSelectChange}
        onLegendMouseover={handleLegendMouseover}
        onLegendMouseout={handleLegendMouseout}
      />
    },
  ];

  const renderList = [
    ...(selectedMetrics?.isHideBarChart
      ? []
      : renderBarChartList.filter(item => !(selectedMetrics?.isHideSum && item.type === 'eyes'))
    ),
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <StairsChart
        height={height - 12}
        // isStackBar={true}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? newLineSeries : []}
        formatterFunc={formatterConfig?.lineStairsFormatter}
        setOtherConfigFunc={formatterConfig?.setOtherConfigFunc}
        handleLegendSelectChange={handleLegendSelectChange}
        onLegendMouseover={handleLegendMouseover}
        onLegendMouseout={handleLegendMouseout}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={height}
        loading={false}
        tableOption={table?.option}
        tableList={table?.data}
      />
    }
  ];

  let correspondingTitleSum: string;

  if (correspondingMetrics?.isNoSum) {
    correspondingTitleSum = correspondingTitle;
  } else if (isEn) {
    correspondingTitleSum = replacePlaceholders('SUM {{metric}}', { metric: correspondingTitle });
  } else {
    correspondingTitleSum = replacePlaceholders('{{metric}}汇总', { metric: correspondingTitle });
  }
  // 使用 CSS 变量把折扣图额外顶部间距传给样式层，避免内联对象在 JSX 中重复创建。
  const cardStyle: React.CSSProperties & Record<'--stairs-extra-top', string> = {
    '--stairs-extra-top': `${stairsExtraTop || 0}px`,
  };

  return (
    <>
      <CardBox
        className={`register-table dbd-sales-net-revenue ${className}`}
        style={cardStyle}
        height={height}
        bordered={false}
        titleCom={<div className='dbd-sales-net-revenue-title-com'>
          {titleCom}
        </div>}
        onChangeChartType={(activeChartTab) => setActiveChartTab(activeChartTab)}
        subTitleCom={
          <div className="dbd-sales-net-revenue-desc">
            {selectedMetrics?.isHideSubTitle ? '' : subTitle?.map((e, i) => (
              <span key={i} className="dbd-desc-item">
                {e.name}: {e.num}
              </span>
            ))}
            {!isHideDimensionSelector && (
              <div>
                <Select
                  listHeight={226}
                  appearance="default"
                  className="overlay-line-select-text"
                  options={SEPARATED_OPTIONS}
                  value={dimensionValue}
                  listWidth={SEPARATED_OPTIONS?.some((item: ISeparatedOption) => item.value === "category") ? 180 : 150}
                  onChange={(value: string) => {
                    setDimensionValue(value);
                  }}
                  placeholder={formatMessage({ id: 'common_select_placeholder' })}
                />
              </div>
            )}
          </div>
        }
        middleContentCom={
          <>
            {
              !isTrendsLoading && !isNodata && !isTrendsError && iconType === 'barChart' && <div className='dbd-sales-net-revenue-title'>
                <span>{title}</span>
                <span>{correspondingMetrics && correspondingTitleSum}</span>
              </div>
            }
            {
              isShow && <div className={`dbd-sales-net-revenue-select ${(iconType === 'barChart' || iconType === 'lineChart') ? '' : 'dbd-sales-net-revenue-noshow'}
            ${iconType === 'lineChart' ? 'dbd-sales-net-revenue-select-line-chat' : ''}
            ${(!subTitle && iconType === 'lineChart') ? 'dbd-sales-net-revenue-select-line-chart-no-desc' : ''}
            'dbd-sales-net-revenue-select-line-chart-no-desc'
            `} style={{ top: selectedMetrics?.countrySelectTop ? selectedMetrics.countrySelectTop[activeChartTab] : undefined }}>
                {
                  timeType === 'Day' &&
                  (<ConfigProvider locale={locale === "en-US" ? 'en' : 'zh'}>
                    <Select
                      matchButtonWidth
                      className='search-item'
                      boxClassName='dbd-sales-net-revenue-select-box'
                      searchable
                      button={<div className={`selector-multiple__header select-button`}>
                        <span className="select-button-text dbd-select-button-text">
                          {formatMessage({ id: 'selector_multiple_component_country_label' })}
                        </span>
                        <span className="select-button-value">
                          {buttonValue}
                        </span>
                      </div>}
                      value={selectedRegion}
                      onChange={(value: string) => {
                        setSelected({});
                        // setRegionValue(value);
                        changeData('selectedRegion', value);
                      }}
                      options={countryOption}
                    />
                  </ConfigProvider>)
                }
              </div>
            }
            {
              isShow && <div className='dbd-sales-net-revenue-nodata'>
                <Abnormal
                  style={{ height: 90, marginRight: '24px' }}
                  component={<div />}
                  isLoading={isDiscountLoading}
                  isError={isDiscountError}
                  isNoData={stairsNodata || !stairsLegend.length}
                  nodataMsg={formatMessage({ id: 'common_discount_nodata' })}
                />
              </div>
            }
          </>
        }
        key={selectedMetrics?.isHideBarChart ? 0 : 1}
        renderList={renderList || []}
        defaultAvtive={selectedMetrics?.isHideBarChart ? 0 : 1}
        setCurrentDataType={(type: string) => {
          if (type === 'eyes') {
            setIseye(!iseye);
          } else {
            setIconType(type);
          }
        }}
        downloadClick={async () => {
          handleDownload(table, title);
        }}
        isLoading={isTrendsLoading}
        isNodata={isNodata}
        isError={isTrendsError}
      >

      </CardBox>
      {LegendTooltipBubble}
    </>
  );
};

export default SalesTrendChart;